import {useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    TextField,
    Typography,
    IconButton
} from "@mui/material";
import {
    Close as CloseIcon,
    StickyNote2 as NoteIcon,
    AttachFileOutlined as AttachIcon,
    DeleteOutlineOutlined as RemoveIcon
} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {uploadDocument} from "../../services/MediaUploadService";
import NoteAttachment from "./NoteAttachment";

/**
 * The ceiling on an attachment.
 *
 * Matches the menu PDF's, and for the same reason: the file streams through the API rather than
 * going direct to S3, so it is bound by the servlet's multipart limit. Refused here with a sentence
 * the trainer can act on, instead of by the container with one they cannot.
 */
const MAX_MB = 20;

const NoteDialog = ({isOpen, close, approve, initValue, title, coach = false}) => {
    const [value, setValue] = useState({...initValue});
    const [progress, setProgress] = useState(null);
    const [fileError, setFileError] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        setValue(isOpen ? {...initValue} : {});
        setProgress(null);
        setFileError("");
    }, [isOpen]);

    const handleChange = (val) => {
        setValue(prv => ({
            ...prv,
            message: val
        }));
    };

    /*
     * The file is uploaded HERE, not on save.
     *
     * Two reasons. The trainer sees it land — a 20MB scan over a gym's wifi is long enough that a
     * save button which silently blocks reads as broken — and by the time they press save the note
     * is two strings, so saving stays the same one request it was before, with the same error path.
     * The cost is an orphaned CDN object if they then close the dialog, which is a file nobody pays
     * attention to rather than a note nobody can open.
     */
    const pickFile = (e) => {
        const file = e.target.files?.[0];
        // let the same file be chosen again after a remove
        e.target.value = "";
        if (!file) return;

        setFileError("");
        if (file.size > MAX_MB * 1024 * 1024) {
            setFileError(`הקובץ גדול מדי — עד ${MAX_MB} מגה.`);
            return;
        }

        setProgress(0);
        uploadDocument(file, (percent) => setProgress(percent))
            .then((url) => {
                setValue(prv => ({...prv, fileUrl: url, fileName: file.name}));
                setProgress(null);
            })
            .catch(() => {
                setFileError("ההעלאה נכשלה. נסה שוב.");
                setProgress(null);
            });
    };

    /*
     * Removing clears BOTH fields, and the save that follows writes the nulls — which is what makes
     * "take the file off this note" possible at all. The server writes what it is given here rather
     * than preserving an absent field.
     */
    const removeFile = () => setValue(prv => ({...prv, fileUrl: null, fileName: null}));

    // A note may be nothing but a file: the document speaks for itself, and refusing to save one
    // would mean typing a sentence describing the thing already attached.
    const canSave = Boolean(value.message?.trim() || value.fileUrl);

    return (
        <Dialog
            open={isOpen}
            onClose={close}
            PaperProps={{
                sx: {
                    borderRadius: coach ? coachT.r : borders.radius.lg,
                    minWidth: {xs: "90%", sm: "420px"},
                    maxWidth: "480px",
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: coach ? coachT.bg : colors.bg,
                        borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <NoteIcon sx={{color: coach ? coachT.t2 : colors.textSecondary, fontSize: "20px"}} />
                            <Typography sx={{
                                fontWeight: coach ? 800 : 600,
                                fontSize: "16px",
                                color: coach ? coachT.text : colors.textPrimary
                            }}>
                                {title || (initValue?.id ? "עריכת הודעה" : "הודעה חדשה")}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={close}
                            size="small"
                            sx={{
                                color: colors.textMuted,
                                "&:hover": {
                                    backgroundColor: colors.bgHover,
                                    color: colors.textPrimary
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "24px 20px"}}>
                        <Typography sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: colors.textSecondary,
                            marginBottom: "8px"
                        }}>
                            תוכן ההודעה
                        </Typography>
                        <TextField
                            fullWidth
                            value={value.message || ""}
                            onChange={(e) => handleChange(e.target.value)}
                            minRows={4}
                            multiline
                            variant="outlined"
                            placeholder="הקלד כאן..."
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: coach ? coachT.rs : borders.radius.md,
                                    backgroundColor: colors.white,
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: coach ? coachT.green : colors.primary
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: coach ? coachT.green : colors.primary,
                                        borderWidth: "2px"
                                    }
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: coach ? coachT.border : colors.border
                                }
                            }}
                        />

                        {/* ── the attachment ── */}
                        <Box sx={{marginTop: "18px"}}>
                            <Typography sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: colors.textSecondary,
                                marginBottom: "8px"
                            }}>
                                קובץ מצורף
                            </Typography>

                            <Box
                                component="input"
                                ref={inputRef}
                                type="file"
                                aria-label="קובץ מצורף"
                                onChange={pickFile}
                                sx={{display: "none"}}
                            />

                            {progress !== null ? (
                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                    <CircularProgress size={18} sx={{color: coach ? coachT.green : colors.primary}} />
                                    <Typography sx={{fontSize: "12.5px", color: colors.textSecondary}}>
                                        מעלה… {progress}%
                                    </Typography>
                                </Box>
                            ) : value.fileUrl ? (
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                                    <NoteAttachment fileUrl={value.fileUrl} fileName={value.fileName} />
                                    <Button
                                        onClick={removeFile}
                                        startIcon={<RemoveIcon />}
                                        size="small"
                                        sx={{
                                            color: colors.textSecondary,
                                            fontSize: "12.5px",
                                            textTransform: "none",
                                            "&:hover": {color: coachT.red, backgroundColor: coachT.redLight}
                                        }}
                                    >
                                        הסר קובץ
                                    </Button>
                                </Box>
                            ) : (
                                <Button
                                    onClick={() => inputRef.current?.click()}
                                    startIcon={<AttachIcon />}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: coach ? coachT.border : colors.border,
                                        color: coach ? coachT.t2 : colors.textSecondary,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        borderRadius: coach ? coachT.rs : borders.radius.md,
                                        "&:hover": {
                                            borderColor: coach ? coachT.green : colors.primary,
                                            backgroundColor: coach ? coachT.bg : colors.bgHover
                                        }
                                    }}
                                >
                                    צרף קובץ
                                </Button>
                            )}

                            {fileError && (
                                <Typography sx={{fontSize: "12.5px", color: coachT.red, marginTop: "8px"}}>
                                    {fileError}
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: 1.5,
                        padding: "16px 20px",
                        borderTop: coach ? `1px solid ${coachT.border}` : borders.default,
                        backgroundColor: coach ? coachT.bg : colors.bg
                    }}>
                        <Button
                            onClick={() => approve({...value})}
                            disabled={!canSave || progress !== null}
                            variant="contained"
                            sx={{
                                backgroundColor: coach ? coachT.green : colors.primary,
                                color: colors.white,
                                fontSize: "13px",
                                fontWeight: 600,
                                padding: "8px 24px",
                                borderRadius: coach ? coachT.rs : borders.radius.md,
                                textTransform: "none",
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: coach ? coachT.greenDark : colors.primaryDark,
                                    boxShadow: "none"
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: colors.border,
                                    color: colors.textMuted
                                }
                            }}
                        >
                            שמור
                        </Button>
                        <Button
                            onClick={close}
                            variant="outlined"
                            sx={{
                                borderColor: coach ? coachT.border : colors.border,
                                color: coach ? coachT.t2 : colors.textSecondary,
                                fontSize: "13px",
                                fontWeight: 500,
                                padding: "8px 20px",
                                borderRadius: coach ? coachT.rs : borders.radius.md,
                                textTransform: "none",
                                "&:hover": {
                                    borderColor: coach ? coachT.t3 : colors.textSecondary,
                                    backgroundColor: coach ? coachT.bg : colors.bgHover
                                }
                            }}
                        >
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default NoteDialog;
